import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import SimpleFileInput from 'assets/components/inputs/file/simpleFileInput/SimpleFileInput';
import ThemeSimpleFileInput from 'assets/components/inputs/file/simpleFileInput/themes/Theme.SimpleFileInput';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { combineStrings } from 'assets/utils/data/String';
import { isValidValue } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty, keys } from 'lodash';
import claimJobDocumentDeleteApi from 'models/claims/claimJobDocument/delete/Api.ClaimJobDocument.Delete';
import claimJobDocumentSaveApi from 'models/claims/claimJobDocument/save/Api.ClaimJobDocument.Save';
import ClaimDocumentPreview from 'module/claims/views/claim/other/Claim.DocumentPreview';
import { useEffect, useState } from 'react';
import { useClaimJobEdit } from './ClaimEdit.Job.Hooks';

type DeleteJobDocumentProps = { documentId: number; claimJobId: number };
export default function ClaimEditJobDocuments() {
  const { lang } = getLocales();
  const {
    reload,
    uploadedDocuments,
    uploadableDocuments,
    claimJob,
    isClaimJobEditableByStatus,
    permissions,
    isClaimAdmin,
  } = useClaimJobEdit();
  const { execute: deleteClaimJobDocument } = useApi({
    action: claimJobDocumentDeleteApi,
    wait: true,
    callback: reload,
  });

  const onJobDocumentDelete = ({ documentId, claimJobId }: DeleteJobDocumentProps) => {
    deleteClaimJobDocument({ claimJobId, documentId });
    window.location.reload();
  };

  //#region Document form editing
  const [document, selectDocument] = useState<Model.IClaimTypeDocument>();
  const [documentsFiles, setDocumentsFiles] = useState<Record<number, Model.IUploadableClaimJobDocument[]>>({});
  const handleFileChange = (files: Hooks.Input.File.Info[]) => {
    const updatedFiles = files?.map((file) => ({
      formFile: file.original,
      newName: file.original.name,
      ...file,
    }));
    setDocumentsFiles((prev) => ({
      ...prev,
      [document.id]: updatedFiles,
    }));
    docForm.update({ formFiles: updatedFiles });
  };
  useEffect(() => {
    selectDocument(uploadableDocuments[0]);
  }, [uploadableDocuments]);
  const docForm = useForm<Model.IClaimJobDocument & { formFiles: Model.IUploadableClaimJobDocument[] }>(
    {
      default: !document
        ? {}
        : {
            name: document.name,
            claimTypeDocumentId: document.id,
            approved: document.approvalRequired ? false : true,
          },
      validation: (data, errors) => {
        if (isEmpty(uploadedDocuments)) {
          errors.formFiles = {};
          if (!data.claimTypeDocumentId) {
            errors.claimTypeDocumentId = lang.required;
          }
          if (document) {
            const hasFiles = !!documentsFiles[document.id]?.length;
            if (document.required && !hasFiles) {
              errors.formFiles._objectError = `${lang.required} for ${document.name}`;
            }
            if (hasFiles) {
              documentsFiles[document.id]?.forEach((formFile, index) => {
                if (!formFile.newName) {
                  errors.formFiles[index] = errors.formFiles[index] || {};
                  errors.formFiles[index].newName = lang.required;
                }
              });
            }
          }
        }
      },
      onSubmit: async (data) => {
        let totalFiles = 0;
        let totalUploadedFiles = 0;
        let totalFailedFiles = 0;

        const documentKeys = keys(documentsFiles) || [];
        for (const key of documentKeys) {
          const claimTypeDoc = uploadableDocuments?.find((it) => it.id === Number(key));
          const filesForUpload = documentsFiles[key] || [];
          for (const file of filesForUpload) {
            totalFiles++;
            const res = await claimJobDocumentSaveApi({
              ...data,
              approved: claimTypeDoc.approvalRequired ? false : true,
              claimTypeDocumentId: claimTypeDoc.id,
              formFile: file.formFile,
              notes: isValidValue(file.notes) ? file.notes : undefined,
              name: file.newName,
              claimJobId: claimJob.id,
            });

            if (res.status === HttpStatus.ok) totalUploadedFiles++;
            else totalFailedFiles++;
          }
        }
        addLog({
          success: `${totalUploadedFiles} out of ${totalFiles} files were uploaded successfully. ${totalFailedFiles} file(s) failed to upload.`,
        });
        reload();
      },
    },
    [document]
  );
  const currentFiles = documentsFiles[document?.id] || [];
  //#endregion

  return !uploadableDocuments.length ? null : (
    <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
      <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>
        {lang.documents}
        <span>
          <SelectInput
            name="docId"
            style={{
              formInput: {
                maxWidth: Theme.Size.pixel(240),
              },
            }}
            placeholder={lang.documents + '...'}
            data={uploadableDocuments?.map((document) => ({
              id: document.id,
              title: combineStrings(' - ', document.name, document.source),
            }))}
            value={document?.id}
            onChange={({ docId }) => selectDocument(uploadableDocuments.find((it) => it.id === docId))}
            icon={<Icon class="fas-file" />}
          />
        </span>
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <br />
        <ClaimDocumentPreview
          showGallery={true}
          documents={uploadedDocuments.map((it) => ({
            id: it.id,
            url: it.mediaFile?.url,
            name: it.name,
            extension: it.mediaFile?.extension,
            mimeType: it.mediaFile?.contentType,
            storedFileName: it.mediaFile?.name,
            description: it.notes,
            approved: it.approved,
            onDelete: () => onJobDocumentDelete({ documentId: it.id, claimJobId: it.claimJobId }),
          }))}
          isDeleteDisabled={!(isClaimAdmin || permissions?.DELETE_CLAIMJOBDOCUMENT)}
          isDownloadDisabled={!(isClaimAdmin || permissions?.DOWNLOAD_CLAIMJOBDOCUMENT)}
        />
        <br />
        <SimpleFileInput
          name="files"
          fileLimit={document?.allowMultiple ? 5 : 1}
          value={currentFiles.map((it) => ({
            original: it.formFile,
            type: it?.formFile?.type as Utils.File.MimeType,
            dataUrl: window.URL.createObjectURL(it.formFile),
            newName: it.newName,
            name: it.formFile?.name,
            notes: it.notes,
          }))}
          isLoading={docForm.isSubmitted}
          onChange={({ files }) => handleFileChange(files)}
          class={ThemeSimpleFileInput}
          disabled={!document || isClaimJobEditableByStatus || !(isClaimAdmin || permissions?.UPLOAD_CLAIMJOBDOCUMENT)}
          error={docForm.errors?.formFiles?._objectError}
          additionalInputs={({ file, index }) => (
            <div key={index}>
              <h6>{file?.name}</h6>
              <TextInput
                name="newName"
                label={lang.name}
                value={file?.newName}
                onChange={({ newName }) => {
                  const updatedFiles = [...currentFiles];
                  updatedFiles[index].newName = newName;
                  setDocumentsFiles((prev) => ({
                    ...prev,
                    [document.id]: updatedFiles,
                  }));
                }}
                error={docForm.errors?.formFiles?.[index]?.newName}
              />
              &nbsp;
              {document?.allowNotes && (
                <MultilineTextInput
                  name="notes"
                  label={lang.notes}
                  value={file?.notes}
                  onChange={({ notes }) => {
                    const updatedFiles = [...currentFiles];
                    updatedFiles[index].notes = notes;
                    setDocumentsFiles((prev) => ({
                      ...prev,
                      [document.id]: updatedFiles,
                    }));
                  }}
                />
              )}
            </div>
          )}
        />
        <br />
        <br />
        <Button
          render={lang.add}
          media={<Icon class="fas-upload" />}
          onClick={docForm.submit}
          disabled={
            docForm.hasErrors ||
            !document ||
            isClaimJobEditableByStatus ||
            !(isClaimAdmin || permissions?.UPLOAD_CLAIMJOBDOCUMENT) ||
            (isEmpty(uploadedDocuments) &&
              uploadableDocuments.some((doc) => doc.required && isEmpty(documentsFiles[doc.id])))
          }
          active={!docForm.hasErrors}
          class={ThemeButtonCircle}
          style={{ marginLeft: 'auto' }}
        />
      </div>
      <br />
    </div>
  );
}
